/**
 * The reference that a failure of the server shows to the user.
 *
 * The API puts an id on every response, in the `X-Request-Id` header. The log
 * of the API has one event for that request, with the same id: the route, the
 * caller, and the error with its stack (api/docs/logging.md). So a user who
 * sends a picture of the error gives support the one thing it needs to find
 * the request.
 *
 * ONLY FOR A 5xx. A 4xx has the API's own sentence, such as «رقم الهاتف
 * مستخدم»: the user can act on it, and a reference adds nothing.
 *
 * The short form is the first 8 characters. In the log, search
 * `requestId:1a2b3c4d*`.
 */

export const REFERENCE_LABEL = "رقم المرجع";

/**
 * What the dashboard shows for the API's general 500 text.
 *
 * The API answers every failure of the server with the English text
 * `internal server error`, and it must: installed apps compare that exact text
 * (api/docs/messages.md). An admin reads Arabic, so the dashboard shows this
 * sentence in its place.
 */
export const SERVER_ERROR_SHOWN = "حدث خطأ في الخادم";

const SERVER_ERROR_TEXT = "internal server error";

/**
 * The API's general 500 text in any of its old spellings (`Internal server
 * error`, `internal server error!`).
 *
 * @param {unknown} message
 * @returns {boolean}
 */
export const isGeneralServerError = (message) =>
    typeof message === "string" &&
    message
        .trim()
        .toLowerCase()
        .replace(/[.!]+$/, "") === SERVER_ERROR_TEXT;

const REQUEST_ID_HEADER = "x-request-id";
const SHORT_LENGTH = 8;

/**
 * @param {import("axios").AxiosError} error
 * @returns {{id: string, short: string} | null} null when the answer is not a
 *   5xx, or has no id (an old API, or a network error with no answer)
 */
export const requestReferenceOf = (error) => {
    const status = error?.response?.status;
    if (!(status >= 500)) return null;

    const headers = error.response.headers;
    const id =
        headers?.[REQUEST_ID_HEADER] ??
        (typeof headers?.get === "function" ? headers.get(REQUEST_ID_HEADER) : null);
    if (typeof id !== "string" || id.trim() === "") return null;

    return { id, short: id.slice(0, SHORT_LENGTH) };
};

/**
 * The message of an error with its reference: `<message> (رقم المرجع: 1a2b3c4d)`.
 * The API's general 500 text becomes «حدث خطأ في الخادم».
 * The message is unchanged when there is no reference.
 *
 * @param {unknown} message
 * @param {import("axios").AxiosError} error
 * @returns {unknown}
 */
export const withRequestReference = (message, error) => {
    const reference = requestReferenceOf(error);
    if (!reference || typeof message !== "string") return message;
    const shown = isGeneralServerError(message) ? SERVER_ERROR_SHOWN : message;
    return `${shown} (${REFERENCE_LABEL}: ${reference.short})`;
};
